import { Button, Modal } from 'common/components';
import { LoaderCircle, ScrollText } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useGetLivekitRoomDetails } from 'services/hooks/queries';
import { LiveKitRoom, type LiveKitRoomProps, useTranscriptions } from '@livekit/components-react';

import { usePostPersistTranscript } from 'services/hooks/queries/usePostPersistTranscript';
import { useGetCallTranscript } from 'services/hooks/mutations/useGetCallTranscript';
import { toast } from 'react-toastify';
import { TranscriptionWithSpeaker, getSpeakerType, Transcripts } from 'components/SyncTranscript';

import styles from './LiveTranscript.module.scss';
// import { getMockTranscript } from 'components/SyncTranscript/mockData';

// Type assertion to fix CRA/TypeScript issue with LiveKitRoom return type
// LiveKitRoom is typed to return ReactNode (can include undefined) but always returns valid JSX in practice
// This is a known issue with CRA and components that return ReactNode instead of JSX.Element
const LiveKitRoomWrapper = LiveKitRoom as unknown as React.FC<LiveKitRoomProps>;

/**
 * LivekitTranscript Component
 *
 * Displays live transcriptions with speaker identification.
 * Differentiates between:
 * - User/Customer: Phone number format (e.g., +12029884976)
 * - Agent: Human agent (UUID format, e.g., 4f40918b-4758-4c17-bcf1-6f45edfa88e7)
 * - AI: AI assistant (e.g., agent-AJ_LCd4Jm427jZr)
 *
 * Speaker identification is based on participantInfo.identity pattern:
 * - Starts with "agent-" → AI assistant
 * - UUID format → Human agent
 * - Phone number format (starts with "+") → Customer/User
 */

interface LivekitTranscriptProps {
    accumulatedTranscriptions: TranscriptionWithSpeaker[];
    onNewTranscriptions?: (transcriptions: TranscriptionWithSpeaker[]) => void;
}

const LivekitTranscript = ({ accumulatedTranscriptions, onNewTranscriptions }: LivekitTranscriptProps) => {
    const transcriptions = useTranscriptions();
    const previousTranscriptionsRef = useRef<Array<{ text: string; participantIdentity: string }>>([]);
    const pendingTranscriptionsRef = useRef<TranscriptionWithSpeaker[] | null>(null);
    const rafIdRef = useRef<number | null>(null);

    // Batch transcription updates using requestAnimationFrame to reduce re-renders
    const flushPendingUpdate = useCallback(() => {
        if (pendingTranscriptionsRef.current !== null && onNewTranscriptions) {
            onNewTranscriptions(pendingTranscriptionsRef.current);
            pendingTranscriptionsRef.current = null;
        }
        rafIdRef.current = null;
    }, [onNewTranscriptions]);

    useEffect(() => {
        if (!transcriptions?.length) return;

        const previousTranscriptions = previousTranscriptionsRef.current;
        let hasChanges = false;

        // Build updated transcriptions array matching the length of incoming transcriptions
        const updatedTranscriptions: TranscriptionWithSpeaker[] = transcriptions.map((transcription, index) => {
            const participantIdentity = transcription.participantInfo?.identity || 'unknown';
            const currentText = transcription.text;
            const previousTranscription = previousTranscriptions[index];

            // Check if this transcription has changed
            const hasChanged =
                !previousTranscription ||
                previousTranscription.text !== currentText ||
                previousTranscription.participantIdentity !== participantIdentity;

            hasChanges = hasChanged;

            // If we already have this transcription in accumulated list, reuse it (updating text if changed)
            const existingTranscription = accumulatedTranscriptions[index];
            if (existingTranscription && !hasChanged) {
                return existingTranscription;
            }

            // Create new or updated transcription
            const timestamp = hasChanged && existingTranscription ? existingTranscription.timestamp : Date.now();
            const id = existingTranscription?.id || `${participantIdentity}-${timestamp}-${Math.random()}`;

            return {
                text: currentText,
                participantIdentity,
                speakerType: getSpeakerType(participantIdentity),
                timestamp,
                id,
            };
        });

        // Update the previous transcriptions snapshot
        previousTranscriptionsRef.current = transcriptions.map((transcription) => ({
            text: transcription.text,
            participantIdentity: transcription.participantInfo?.identity || 'unknown',
        }));

        // Only update if there are changes
        if (hasChanges || updatedTranscriptions.length !== accumulatedTranscriptions.length) {
            pendingTranscriptionsRef.current = updatedTranscriptions;

            // Schedule batched update if not already scheduled
            if (rafIdRef.current === null) {
                rafIdRef.current = requestAnimationFrame(flushPendingUpdate);
            }
        }
    }, [transcriptions, accumulatedTranscriptions, flushPendingUpdate]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
            }
        };
    }, []);

    return <Transcripts transcriptions={accumulatedTranscriptions} containerClassName={styles.transcriptContainer} />;
};

export const LiveTranscript = ({ callID, isCallInProgress }: { callID: string; isCallInProgress: boolean }) => {
    const [open, setOpen] = useState(false);
    const [accumulatedTranscriptions, setAccumulatedTranscriptions] = useState<TranscriptionWithSpeaker[]>([]);
    const [persistedTranscriptions, setPersistedTranscriptions] = useState<TranscriptionWithSpeaker[]>([]);
    const transcriptContainerRef = useRef<HTMLDivElement | null>(null);

    const { livekitRoomDetails, isLoadingLivekitRoomDetails } = useGetLivekitRoomDetails(callID, open);
    const {
        isLoading: isLoadingPersistTranscript,
        isPersistTranscriptSuccess,
        isFetched: isFetchedPersistTranscript,
    } = usePostPersistTranscript(livekitRoomDetails?.Room, open);
    const { getCallTranscriptWithRetry, isCallTranscriptWithRetryLoading } = useGetCallTranscript();

    useEffect(() => {
        if (isPersistTranscriptSuccess && open) {
            const timer = setTimeout(() => {
                getCallTranscriptWithRetry(callID, {
                    onSuccess: (transcriptData) => {
                        const formattedTranscriptions = transcriptData.speeches.map((speech) => ({
                            text: speech.text,
                            participantIdentity: speech.speaker,
                            speakerType: getSpeakerType(speech.speaker),
                            timestamp: speech.timestamp_ns,
                            id: speech.order.toString(),
                        }));
                        setPersistedTranscriptions(formattedTranscriptions);
                    },
                    onError: () => {
                        toast.error('Error fetching history transcriptions');
                    },
                });
            }, 1000);

            return () => clearTimeout(timer);
        }
        // else {
        //     const formattedTranscriptions = getMockTranscript().map((speech) => ({
        //         text: speech.text + '100',
        //         participantInfo: { identity: speech.speaker },
        //         participantIdentity: speech.speaker,
        //         speakerType: getSpeakerType(speech.speaker),
        //         timestamp: speech.timestamp_ns,
        //         id: speech.order.toString() + '100',
        //     }));
        //     if (!isLoadingPersistTranscript) {
        //         setPersistedTranscriptions(formattedTranscriptions);
        //     }
        //     // console.log('formattedTranscriptions', formattedTranscriptions);
        //     // setAccumulatedTranscriptions((prev) => [...formattedTranscriptions, ...prev]);
        // }
    }, [isPersistTranscriptSuccess, open]);

    useEffect(() => {
        if (!open) {
            setAccumulatedTranscriptions([]);
            setPersistedTranscriptions([]);
        }
    }, [open]);

    const handleUpdateTranscriptions = useCallback((updatedTranscriptions: TranscriptionWithSpeaker[]) => {
        setAccumulatedTranscriptions(updatedTranscriptions);
    }, []);

    // Scroll to bottom after DOM updates with new transcriptions (setState is async, so scroll must run after commit)
    useEffect(() => {
        if (accumulatedTranscriptions.length === 0 || !transcriptContainerRef.current) return;

        transcriptContainerRef.current?.scrollTo({
            top: transcriptContainerRef.current.scrollHeight,
            behavior: 'smooth',
        });
    }, [accumulatedTranscriptions]);

    return (
        <div>
            <Button
                icon={ScrollText}
                iconOnly
                isRounded
                onClick={(event) => {
                    if (!isCallInProgress) return;
                    event.stopPropagation();
                    setOpen(true);
                }}
                disabled={!isCallInProgress}
            />

            <Modal
                isOpen={open}
                onClose={() => setOpen(false)}
                title='Live Transcript'
                isLoadingOverlay={isLoadingLivekitRoomDetails}
            >
                <div
                    className={styles.transcriptContainer}
                    style={{ maxHeight: '500px', padding: 'var(--spacing-lg)', overflowY: 'auto' }}
                    ref={transcriptContainerRef}
                >
                    {isCallTranscriptWithRetryLoading || isLoadingPersistTranscript ? (
                        <div className={styles.loadingTranscriptions}>
                            <LoaderCircle size={18} className={styles.spinner} color='var(--primary-purple)' /> Loading
                            history transcriptions...
                        </div>
                    ) : (
                        <Transcripts
                            transcriptions={persistedTranscriptions}
                            containerClassName={styles.transcriptContainer}
                        />
                    )}

                    {livekitRoomDetails?.URL && livekitRoomDetails?.Token ? (
                        <LiveKitRoomWrapper
                            serverUrl={livekitRoomDetails.URL}
                            token={livekitRoomDetails.Token}
                            connect={open && isFetchedPersistTranscript}
                            onConnected={() => {
                                console.log('Connected to livekit');
                            }}
                            onDisconnected={() => {
                                console.log('Disconnected from livekit');
                            }}
                        >
                            <LivekitTranscript
                                accumulatedTranscriptions={accumulatedTranscriptions}
                                onNewTranscriptions={handleUpdateTranscriptions}
                            />
                        </LiveKitRoomWrapper>
                    ) : isLoadingLivekitRoomDetails ? (
                        <div>Loading room details...</div>
                    ) : (
                        <div>Unable to load room details</div>
                    )}
                </div>
            </Modal>
        </div>
    );
};
